/**
 * Configuration d'authentification pour TxApp - Production Ready
 */

// Configuration de l'API - logique robuste pour la production
export const JWT_HOST_API = (() => {
  const env = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : {};
  const isDev = env.DEV || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
  
  // 1. En développement local, on laisse le baseURL vide car les services incluent déjà "/api"
  // Vite captera "/api" et le proxiera vers le backend configuré.
  if (isDev) {
    return "";
  }

  // 2. Variable d'environnement pour la production
  const envUrl = env.VITE_API_URL || env.VITE_API_BASE_URL;
  if (envUrl) {
    let cleaned = envUrl.replace(/\/api$/, '').replace(/\/$/, '');
    return cleaned;
  }

  // 3. Fallback production
  return "https://api.txapp.be";
})();

// Détecter environnement production de manière robuste
export const isProduction = () => {
  const env = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : {};
  
  // Priorité au localhost (développement)
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') return false;
  if (typeof window !== 'undefined' && window.location.hostname === '127.0.0.1') return false;

  // Détecter les environnements Cloudflare Pages (production)
  if (typeof window !== 'undefined' && (
    window.location.hostname.includes('pages.dev') ||
    window.location.hostname.includes('txapp.be') ||
    window.location.hostname.includes('txapp-')
  )) return true;

  // Priorité au mode développement
  if (env.DEV) return false;

  // 1. Variable d'environnement Vite explicite
  if (env.VITE_API_BASE_URL) return true;

  // 2. Mode Vite (fallback)
  if (env.PROD) return true;

  // 3. NODE_ENV (dernière chance)
  if (env.MODE === 'production') return true;

  return false;
};



// Token storage key pour localStorage
export const TOKEN_STORAGE_KEY = "txapp-auth-token";

// Session storage key pour les données temporaires
export const SESSION_STORAGE_KEY = "txapp-session";

// Types d'utilisateurs - alignés avec la contrainte DB
export const USER_TYPES = {
  ADMIN: "ADMIN",
  GESTIONNAIRE: "GESTIONNAIRE",
  CHAUFFEUR: "CHAUFFEUR",
  CLIENT: "CLIENT"
};

// Permissions par type d'utilisateur (Clés alignées avec les rôles applicatifs)
export const USER_PERMISSIONS = {
  SUPER_ADMIN: ['all'],
  ADMIN: [
    'user.create', 'user.read', 'user.update', 'user.delete',
    'chauffeur.create', 'chauffeur.read', 'chauffeur.update', 'chauffeur.delete',
    'vehicule.create', 'vehicule.read', 'vehicule.update', 'vehicule.delete',
    'course.create', 'course.read', 'course.update', 'course.delete',
    'feuille_route.create', 'feuille_route.read', 'feuille_route.update', 'feuille_route.delete',
    'client.create', 'client.read', 'client.update', 'client.delete',
    'partner.create', 'partner.read', 'partner.update', 'partner.delete',
    'rapport.read', 'rapport.export', 'accounting.read'
  ],
  ACCOUNTANT: [
    'course.read', 'feuille_route.read', 'client.read',
    'rapport.read', 'rapport.export', 'accounting.read', 'accounting.update',
    'invoice.create', 'invoice.read', 'invoice.update'
  ],
  SUPPORT: [
    'user.read', 'chauffeur.read', 'vehicule.read',
    'course.read', 'feuille_route.read', 'client.read',
    'support.ticket.read', 'support.ticket.update'
  ],
  DRIVER: [
    'course.read', 'course.update', 'course.create',
    'feuille_route.read', 'feuille_route.update', 'feuille_route.create',
    'profile.read', 'profile.update'
  ],
  CHAUFFEUR: [ // Alias for DRIVER
    'course.read', 'course.update', 'course.create',
    'feuille_route.read', 'feuille_route.update', 'feuille_route.create',
    'profile.read', 'profile.update'
  ],
  CLIENT: [
    'course.create', 'course.read', 'course.update',
    'profile.read', 'profile.update'
  ]
};

// Routes publiques (accessibles sans authentification)
export const PUBLIC_ROUTES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/health',
  '/api/health'
];

// Configuration de sécurité
export const SECURITY_CONFIG = {
  maxLoginAttempts: 5,
  lockoutDuration: 15 * 60 * 1000, // 15 minutes
  passwordMinLength: 8,
  passwordRequireSpecialChar: true,
  passwordRequireNumber: true,
  passwordRequireUppercase: true,
  sessionSecure: (typeof import.meta !== 'undefined' && import.meta.env)?.PROD || false, // HTTPS seulement en production
  sameSite: 'strict'
};

// Messages d'erreur d'authentification
export const AUTH_ERRORS = {
  INVALID_CREDENTIALS: 'Identifiants invalides',
  ACCOUNT_LOCKED: 'Compte verrouillé temporairement',
  SESSION_EXPIRED: 'Session expirée, veuillez vous reconnecter',
  INSUFFICIENT_PERMISSIONS: 'Permissions insuffisantes',
  NETWORK_ERROR: 'Erreur de connexion au serveur',
  TOKEN_INVALID: 'Token d\'authentification invalide',
  USER_NOT_FOUND: 'Utilisateur non trouvé',
  WEAK_PASSWORD: 'Mot de passe trop faible'
};

// Points de terminaison d'authentification
export const AUTH_ENDPOINTS = {
  LOGIN: '/api/auth/login',
  LOGOUT: '/api/auth/logout',
  REGISTER: '/api/auth/register',
  REFRESH: '/api/auth/refresh',
  VERIFY: '/api/auth/verify',
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  RESET_PASSWORD: '/api/auth/reset-password',
  CHANGE_PASSWORD: '/api/auth/change-password',
  PROFILE: '/api/auth/profile'
};
